/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { Button, Spinner, CustomDropDown } from '../../../shared/components';
import { Cell } from '../../../shared/components/Table';
import {
  CustomFilterForm,
  CustomFilterItem,
  CustomFilterOperations,
} from '../../../services/types/CustomFilter';
import SimpleColumnChecks from '../SimpleColumnChecks';
import userService from '../../../services/userService';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import reducer, { SearchUserAction } from './UserQueryReducer';
import { Year } from '../../../services/types/Misc';
import reducerYear, { SearchYearAction } from './YearQueryReducer';
import { User } from '../../../services/types/User';

import 'react-datepicker/dist/react-datepicker.css';
import AutocompleteChips from '../../../shared/components/AutocompleteChips';
import operationService from '../../../services/operationService';

interface Props {
  handleSubmit: (values: CustomFilterForm, customFilterOperation: CustomFilterOperations) => void;
  handleCancel: () => void;
  formState?: CustomFilterForm | null;
  customFilterMetaData: CustomFilterForm;
  availableFields: string[];
}

type FilterOption = Cell<CustomFilterItem>;

type CustomerFilterOptions =
  | 'esgStatus'
  | 'phase'
  | 'groups'
  | 'stages'
  | 'types'
  | 'espsPolicies'
  | 'countries'
  | 'esgDocumentStatus'
  | 'divisions'
  | 'impactCategories'
  | 'esrr'
  | 'safeguardPerformances'
  | 'disasterRiskClassifications'
  | 'assessmentsDisclosures'
  | 'esgIndicators';

const FORM_DATE_PICKER_BASE = 'form__form-group-field form__date-picker-wrapper';
const FORM_DATE_PICKER_REQUIRED_SUFFIX = 'input-date-picker-required';

const FORM_DATE_PICKER = FORM_DATE_PICKER_BASE;
const FORM_DATE_PICKER_REQUIRED = `${FORM_DATE_PICKER_BASE} ${FORM_DATE_PICKER_REQUIRED_SUFFIX}`;

const mapToEsgStatus = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.esgStatus } as FilterOption);
const mapToPhase = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.phase } as FilterOption);
const mapToStage = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.stage } as FilterOption);
const mapToTypes = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.type } as FilterOption);
const mapToEspsPolicies = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.espsPolicy } as FilterOption);
const mapToCountries = (item: string): FilterOption =>
  ({
    name: item,
    tPrefix: 'countryCode.',
    value: (v) => v.country,
  } as FilterOption);
const mapToEsgDocumentStatus = (item: string): FilterOption =>
  ({
    name: item,
    tPrefix: 'esgDocumentStatus.',
    value: (v) => v.esgDocumentalStatus,
  } as FilterOption);
const mapToDivisions = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.division } as FilterOption);
const mapToImpactCategories = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.impactCategory } as FilterOption);
const mapToEsrr = (item: string): FilterOption =>
  ({ name: item, tPrefix: 'esrrCodes.', value: (v) => v.esrr } as FilterOption);
const mapToGroups = (item: string): FilterOption =>
  ({ name: item, tPrefix: 'groupCodes.', value: (v) => v.groups } as FilterOption);
const mapToSafeguardPerformances = (item: string): FilterOption =>
  ({ name: item, tPrefix: 'sfpCodes.', value: (v) => v.safeguardPerformance } as FilterOption);
const mapToDisasterRiskClassification = (item: string): FilterOption =>
  ({ name: item, value: (v) => v.disasterRiskClassification } as FilterOption);
const mapToAssessmentsDisclosures = (item: string): FilterOption =>
  ({
    name: item,
    tPrefix: 'assessmentsDisclosures.',
    value: (v) => v.assessmentsDisclosure,
  } as FilterOption);
const mapToEsgIndicators = (item: string): FilterOption =>
  ({
    name: item,
    value: (v) => v.esgIndicators,
  } as FilterOption);

const getFilterOptions = (filters: CustomFilterForm): { [key: string]: FilterOption[] } => ({
  esgStatus: filters.esgStatus.map(mapToEsgStatus),
  phase: filters.phase.map(mapToPhase),
  stages: filters.stages.map(mapToStage),
  types: filters.types.map(mapToTypes),
  espsPolicies: filters.espsPolicies.map(mapToEspsPolicies),
  countries: filters.countries.map(mapToCountries),
  esgDocumentStatus: filters.esgDocumentStatus.map(mapToEsgDocumentStatus),
  divisions: filters.divisions.map(mapToDivisions),
  impactCategories: filters.impactCategories.map(mapToImpactCategories),
  esrr: filters.esrr.map(mapToEsrr),
  groups: filters.groups.map(mapToGroups),
  safeguardPerformances: filters.safeguardPerformances.map(mapToSafeguardPerformances),
  disasterRiskClassifications: filters.disasterRiskClassifications.map(
    mapToDisasterRiskClassification
  ),
  assessmentsDisclosures: filters.assessmentsDisclosures.map(mapToAssessmentsDisclosures),
  esgIndicators: filters.esgIndicators.map(mapToEsgIndicators),
});

export const buildCustomFiltersSchema = (t: (key: string) => string) =>
  Yup.object({
    ermDateFinish: Yup.string().when('ermDateStart', {
      is: (cat: unknown) => cat !== null,
      then: (schema) => schema.concat(Yup.string().required(t('required'))),
      otherwise: (schema) => schema.concat(Yup.string().nullable()),
    }),
    qrrDateFinish: Yup.string().when('qrrDateStart', {
      is: (cat: unknown) => cat !== null,
      then: (schema) => schema.concat(Yup.string().required(t('required'))),
      otherwise: (schema) => schema.concat(Yup.string().nullable()),
    }),
    approvalDateFinish: Yup.string().when('approvalDateStart', {
      is: (cat: unknown) => cat !== null,
      then: (schema) => schema.concat(Yup.string().required(t('required'))),
      otherwise: (schema) => schema.concat(Yup.string().nullable()),
    }),
    closingDateFinish: Yup.string().when('closingDateStart', {
      is: (cat: unknown) => cat !== null,
      then: (schema) => schema.concat(Yup.string().required(t('required'))),
      otherwise: (schema) => schema.concat(Yup.string().nullable()),
    }),
  });

const CustomFiltersForm = (props: Props) => {
  const { handleSubmit, handleCancel, formState, customFilterMetaData, availableFields } = props;
  const { t } = useTranslation();
  const [members, loadingMembers, , dispatch] = useServiceReducer(userService.getUsers, reducer, {
    query: '',
    convergence: true,
    includeGS: true,
  });

  const [years, loadingYears, , dispatchYear] = useServiceReducer(
    operationService.getYearsSearch,
    reducerYear,
    {
      query: '',
    }
  );

  const [customFilterOperation, setCustomFilterOperation] = useState<
    CustomFilterOperations | undefined
  >();

  const formInit: CustomFilterForm = {
    operationNumber: formState ? formState.operationNumber : '',
    operationName: formState ? formState.operationName : '',
    esgStatus: formState ? formState.esgStatus : [],
    phase: formState ? formState.phase : [],
    stages: formState ? formState.stages : [],
    types: formState ? formState.types : [],
    espsPolicies: formState ? formState.espsPolicies : [],
    countries: formState ? formState.countries : [],
    members: formState ? formState.members : [],
    years: formState ? formState.years : [],
    esgDocumentStatus: formState ? formState.esgDocumentStatus : [],
    divisions: formState ? formState.divisions : [],
    impactCategories: formState ? formState.impactCategories : [],
    esrr: formState ? formState.esrr : [],
    groups: formState ? formState.groups : [],
    safeguardPerformances: formState ? formState.safeguardPerformances : [],
    disasterRiskClassifications: formState ? formState.disasterRiskClassifications : [],
    assessmentsDisclosures: formState ? formState.assessmentsDisclosures : [],
    esgIndicators: formState ? formState.esgIndicators : [],
    ermDateStart: formState ? formState.ermDateStart : null,
    ermDateFinish: formState ? formState.ermDateFinish : null,
    closingDateStart: formState ? formState.closingDateStart : null,
    closingDateFinish: formState ? formState.closingDateFinish : null,
    qrrDateStart: formState ? formState.qrrDateStart : null,
    qrrDateFinish: formState ? formState.qrrDateFinish : null,
    approvalDateStart: formState ? formState.approvalDateStart : null,
    approvalDateFinish: formState ? formState.approvalDateFinish : null,
    queryDate: formState ? formState.queryDate : null,
  };

  const formik = useFormik({
    initialValues: { ...formInit },
    validationSchema: buildCustomFiltersSchema(t),
    onSubmit: (values) => {
      const payload: CustomFilterForm = { ...values };
      if (customFilterOperation) {
        handleSubmit(payload, customFilterOperation);
      }
    },
  });

  const [hideColumns, setHideColumns] = useState<{ [key: string]: string[] }>({
    esgStatus: [],
    phase: [],
    stages: [],
    types: [],
    espsPolicies: [],
    countries: [],
    esgDocumentStatus: [],
    divisions: [],
    impactCategories: [],
    esrr: [],
    groups: [],
    safeguardPerformances: [],
    disasterRiskClassifications: [],
    assessmentsDisclosures: [],
    esgIndicators: [],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dropdownOptions, setDropdownOptions] = useState<any | undefined>();

  useEffect(() => {
    if (customFilterMetaData) {
      const formattedOptions = getFilterOptions(customFilterMetaData);
      const initialHiddenColumns = Object.keys(formattedOptions).reduce(
        (acc: { [key: string]: string[] }, cur: string) => {
          acc[cur] = formattedOptions[cur]
            .filter((o: FilterOption) => !!o.name)
            .map((o: FilterOption) => o.name as string);
          return acc;
        },
        {}
      );

      setHideColumns(initialHiddenColumns);
      setDropdownOptions(formattedOptions);
    }
  }, [customFilterMetaData]);

  useEffect(() => {
    if (formState && dropdownOptions) {
      // uncheck all dropdown options except user selections
      const formattedOptions = { ...dropdownOptions };
      const initialHiddenColumns = Object.keys(formattedOptions).reduce(
        (acc: { [key: string]: string[] }, cur: string) => {
          acc[cur] = formattedOptions[cur]
            .filter(
              (o: FilterOption) =>
                !formState[cur as CustomerFilterOptions] ||
                (o.name && !formState[cur as CustomerFilterOptions].includes(o.name))
            )
            .map((o: FilterOption) => o.name || undefined);

          return acc;
        },
        {} as { [key: string]: string[] }
      );
      setHideColumns(initialHiddenColumns);
    } else if (!formState && dropdownOptions) {
      // uncheck all dropdown options
      const formattedOptions = { ...dropdownOptions };
      const initialHiddenColumns = Object.keys(formattedOptions).reduce(
        (acc: { [key: string]: string[] }, cur: string) => {
          acc[cur] = formattedOptions[cur].map((o: FilterOption) => o.name);
          return acc;
        },
        {}
      );
      setHideColumns(initialHiddenColumns);
    }
  }, [formState, dropdownOptions]);

  const updateDropDownValues = (key: string, updatedHiddenOptions: { [key: string]: string[] }) => {
    const keyOptions = dropdownOptions[key].map((o: FilterOption) => o.name);
    const unselectedOptions = updatedHiddenOptions[key];
    const selectedOptions = keyOptions.filter((o: string) => !unselectedOptions.includes(o));
    formik.setFieldValue(key, selectedOptions);
  };

  const setHideColumn = (id: string, checked: boolean, key: string) => {
    if (!checked) {
      const selectdItem = hideColumns[key].find((colId) => colId === id);
      if (!selectdItem) {
        const updatedHiddenOptions = {
          ...hideColumns,
          [key]: [...hideColumns[key], id],
        };
        updateDropDownValues(key, updatedHiddenOptions);
        setHideColumns(updatedHiddenOptions);
      }
    } else {
      const updatedHiddenOptions = {
        ...hideColumns,
        [key]: hideColumns[key].filter((colId) => colId !== id),
      };
      updateDropDownValues(key, updatedHiddenOptions);
      setHideColumns(updatedHiddenOptions);
    }
  };

  const handleEgsMembersSearch = (egsMemberQuery: string) => {
    const action = {
      type: 'QUERY_USER',
      query: egsMemberQuery,
      convergence: true,
    } as SearchUserAction;
    dispatch(action);
  };

  const handleEgsMembersChange = (selected: User[]) => {
    const formattedEgsMembers = selected.map((user) => ({
      email: user.email,
      fullName: user.fullName,
    }));
    formik.setFieldValue('members', formattedEgsMembers);
  };

  // todo

  const handleYearsSearch = (yearQuery: string) => {
    const action = {
      type: 'QUERY_YEAR',
      query: yearQuery,
      convergence: true,
    } as SearchYearAction;
    dispatchYear(action);
  };

  const handleYearsChange = (selected: Year[]) => {
    const formattedEgsMembers = selected.map((year) => ({
      value: year.value,
      text: year.text,
    }));
    formik.setFieldValue('years', formattedEgsMembers);
  };
  //
  const applyAndSaveFilterHandler = () => {
    setCustomFilterOperation(CustomFilterOperations.ApplyAndSaveFilter);
    formik.handleSubmit();
  };

  const applyFilterHandler = () => {
    setCustomFilterOperation(CustomFilterOperations.ApplyFilter);
    formik.handleSubmit();
  };

  const onCancel = () => handleCancel();

  const showFields = {
    operationNumber: availableFields.includes('number'),
    operationName: availableFields.includes('name'),
    esgStatus: availableFields.includes('esgStatus'),
    phase: availableFields.includes('phase'),
    stages: availableFields.includes('stage'),
    members: availableFields.includes('esgMembers'),
    years: availableFields.includes('operationYear'),
    lendingType: availableFields.includes('lendingType'),
    esgDocumentStatus: availableFields.includes('esgDocumentStatus'),
    divisions: availableFields.includes('sectorSubSector'),
    impactCategories: availableFields.includes('esic'),
    esrr: availableFields.includes('esrr'),
    espsPolicies: availableFields.includes('espsPolicies'),
    groups: availableFields.includes('groupName'),
    safeguardPerformances: availableFields.includes('spr'),
    disasterRiskClassifications: availableFields.includes('dccrc'),
    assessmentsDisclosures: availableFields.includes('assessmentsDisclosure'),
    ermDate: availableFields.includes('ermDate'),
    closingDate: availableFields.includes('closingDate'),
    queryDate: availableFields.includes('queryDate'),
    qrrDate: availableFields.includes('qrrDate'),
    approvalDate: availableFields.includes('approvalDate'),
    esgIndicators: availableFields.includes('esgIndicators'),
    DatesAndMissionsTitle: () =>
      showFields.ermDate || showFields.closingDate || showFields.qrrDate || showFields.approvalDate,
  };

  return (
    <form className="form">
      <div className="modal__body">
        <div className="finish-modal modal-dialog--custom-filter">
          <p className="h4">{t('generalInformation')}</p>
          <div className="form-row">
            {showFields.operationNumber && (
              <div className="col-md-3">
                <label className="col-form-label">{t('operationNumber')}</label>
                <input
                  id="operationNumber"
                  placeholder="Ex. AR-12"
                  name="operationNumber"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.operationNumber}
                  className="custom-filter-form"
                />
              </div>
            )}
            {showFields.operationName && (
              <div className="col-md-3">
                <label className="col-form-label">{t('operationName')}</label>
                <input
                  id="operationName"
                  placeholder="Ex. Dam construction"
                  name="operationName"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.operationName}
                  className="custom-filter-form"
                />
              </div>
            )}
            {showFields.years && (
              <div className="col-md-3">
                <AutocompleteChips
                  labelKey="text"
                  htmlId="egs-year-typeahead"
                  text={t('operationYear')}
                  placeholder="Ex. 2020"
                  onSearch={handleYearsSearch}
                  onChange={handleYearsChange}
                  list={years}
                  loading={loadingYears}
                  render={(option: Year) => <div key={option.value}>{option.text}</div>}
                  multiple
                  selected={formik.values.years}
                  className="col-form-label"
                />
              </div>
            )}
            <div className="col-md-3">
              <label className="col-form-label">{t('type_modality')}</label>
              <CustomDropDown
                text={
                  formik.values.types && formik.values.types.length > 0
                    ? `${formik.values.types.length} ${t('selected')}`
                    : t('selectTheOptions')
                }
                className="custom-filters-dropdown"
              >
                <SimpleColumnChecks
                  cells={dropdownOptions ? dropdownOptions.types : []}
                  hideColumns={hideColumns.types}
                  setHideColumn={(id, checked) => setHideColumn(id, checked, 'types')}
                />
              </CustomDropDown>
            </div>
          </div>

          <div className="form-row">
            {showFields.esgStatus && (
              <div className="col-md-3">
                <label className="col-form-label">{t('status')}</label>
                <CustomDropDown
                  text={
                    formik.values.esgStatus && formik.values.esgStatus.length > 0
                      ? `${formik.values.esgStatus.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.esgStatus : []}
                    hideColumns={hideColumns.esgStatus}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'esgStatus')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.phase && (
              <div className="col-md-3">
                <label className="col-form-label">{t('phase')}</label>
                <CustomDropDown
                  text={
                    formik.values.phase && formik.values.phase.length > 0
                      ? `${formik.values.phase.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.phase : []}
                    hideColumns={hideColumns.phase}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'phase')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.stages && (
              <div className="col-md-3">
                <label className="col-form-label">{t('stage')}</label>
                <CustomDropDown
                  text={
                    formik.values.stages && formik.values.stages.length > 0
                      ? `${formik.values.stages.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.stages : []}
                    hideColumns={hideColumns.stages}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'stages')}
                  />
                </CustomDropDown>
              </div>
            )}
            <div className="col-md-3">
              <label className="col-form-label">{t('regionCountry')}</label>
              <CustomDropDown
                text={
                  formik.values.countries && formik.values.countries.length > 0
                    ? `${formik.values.countries.length} ${t('selected')}`
                    : t('selectTheOptions')
                }
                className="custom-filters-dropdown"
              >
                <SimpleColumnChecks
                  cells={dropdownOptions ? dropdownOptions.countries : []}
                  hideColumns={hideColumns.countries}
                  setHideColumn={(id, checked) => setHideColumn(id, checked, 'countries')}
                />
              </CustomDropDown>
            </div>
          </div>

          <div className="form-row">
            {showFields.divisions && (
              <div className="col-md-3">
                <label className="col-form-label">{t('sectorSubSector')}</label>
                <CustomDropDown
                  text={
                    formik.values.divisions && formik.values.divisions.length > 0
                      ? `${formik.values.divisions.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.divisions : []}
                    hideColumns={hideColumns.divisions}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'divisions')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.members && (
              <div className="col-md-3">
                <AutocompleteChips
                  labelKey="fullName"
                  htmlId="egs-members-typeahead"
                  text={t('membersAndGS')}
                  placeholder="Ex. Emma Jackson"
                  onSearch={handleEgsMembersSearch}
                  onChange={handleEgsMembersChange}
                  list={members}
                  loading={loadingMembers}
                  render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                  multiple
                  selected={formik.values.members}
                  className="col-form-label"
                />
              </div>
            )}
            {showFields.groups && (
              <div className="col-md-3">
                <label className="col-form-label">{t('esgGroup')}</label>
                <CustomDropDown
                  text={
                    formik.values.groups && formik.values.groups.length > 0
                      ? `${formik.values.groups.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.groups : []}
                    hideColumns={hideColumns.groups}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'groups')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.assessmentsDisclosures && (
              <div className="col-md-3">
                <label className="col-form-label">{t('assessmentsDisclosure')}</label>
                <CustomDropDown
                  text={
                    formik.values.assessmentsDisclosures &&
                    formik.values.assessmentsDisclosures.length > 0
                      ? `${formik.values.assessmentsDisclosures.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.assessmentsDisclosures : []}
                    hideColumns={hideColumns.assessmentsDisclosures}
                    setHideColumn={(id, checked) =>
                      setHideColumn(id, checked, 'assessmentsDisclosures')
                    }
                  />
                </CustomDropDown>
              </div>
            )}
          </div>

          <hr />

          <p className="h4">{t('esInformation')}</p>
          <div className="form-row">
            {showFields.impactCategories && (
              <div className="col-md-3">
                <label className="col-form-label">{t('impactCategory')}</label>
                <CustomDropDown
                  text={
                    formik.values.impactCategories && formik.values.impactCategories.length > 0
                      ? `${formik.values.impactCategories.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.impactCategories : []}
                    hideColumns={hideColumns.impactCategories}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'impactCategories')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.disasterRiskClassifications && (
              <div className="col-md-3">
                <label className="col-form-label">{t('disasterRiskClassification')}</label>
                <CustomDropDown
                  text={
                    formik.values.disasterRiskClassifications &&
                    formik.values.disasterRiskClassifications.length > 0
                      ? `${formik.values.disasterRiskClassifications.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.disasterRiskClassifications : []}
                    hideColumns={hideColumns.disasterRiskClassifications}
                    setHideColumn={(id, checked) =>
                      setHideColumn(id, checked, 'disasterRiskClassifications')
                    }
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.esrr && (
              <div className="col-md-3">
                <label className="col-form-label">{t('esrr')}</label>
                <CustomDropDown
                  text={
                    formik.values.esrr && formik.values.esrr.length > 0
                      ? `${formik.values.esrr.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.esrr : []}
                    hideColumns={hideColumns.esrr}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'esrr')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.safeguardPerformances && (
              <div className="col-md-3">
                <label className="col-form-label">{t('spr')}</label>
                <CustomDropDown
                  text={
                    formik.values.safeguardPerformances &&
                    formik.values.safeguardPerformances.length > 0
                      ? `${formik.values.safeguardPerformances.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.safeguardPerformances : []}
                    hideColumns={hideColumns.safeguardPerformances}
                    setHideColumn={(id, checked) =>
                      setHideColumn(id, checked, 'safeguardPerformances')
                    }
                  />
                </CustomDropDown>
              </div>
            )}
          </div>

          <div className="form-row">
            {showFields.espsPolicies && (
              <div className="col-md-3">
                <label className="col-form-label">{t('espsPolicies')}</label>
                <CustomDropDown
                  text={
                    formik.values.espsPolicies && formik.values.espsPolicies.length > 0
                      ? `${formik.values.espsPolicies.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.espsPolicies : []}
                    hideColumns={hideColumns.espsPolicies}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'espsPolicies')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.esgIndicators && (
              <div className="col-md-3">
                <label className="col-form-label">ESG Indicators</label>
                <CustomDropDown
                  text={
                    formik.values.esgIndicators && formik.values.esgIndicators.length > 0
                      ? `${formik.values.esgIndicators.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.esgIndicators : []}
                    hideColumns={hideColumns.esgIndicators}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'esgIndicators')}
                  />
                </CustomDropDown>
              </div>
            )}
            {showFields.esgDocumentStatus && (
              <div className="col-md-3">
                <label className="col-form-label">{t('documentStatus')}</label>
                <CustomDropDown
                  text={
                    formik.values.esgDocumentStatus && formik.values.esgDocumentStatus.length > 0
                      ? `${formik.values.esgDocumentStatus.length} ${t('selected')}`
                      : t('selectTheOptions')
                  }
                  className="custom-filters-dropdown"
                >
                  <SimpleColumnChecks
                    cells={dropdownOptions ? dropdownOptions.esgDocumentStatus : []}
                    hideColumns={hideColumns.esgDocumentStatus}
                    setHideColumn={(id, checked) => setHideColumn(id, checked, 'esgDocumentStatus')}
                  />
                </CustomDropDown>
              </div>
            )}
          </div>

          {showFields.DatesAndMissionsTitle() && (
            <>
              <hr />
              <p className="h4">{t('datesAndMilestones')}</p>
            </>
          )}
          <div className="form-row">
            {showFields.ermDate && (
              <div className="col-md-3">
                <label className="col-form-label">{t('ermDate')}</label>
                <div className="input-group input-group-date-picker-wrapper">
                  <div
                    className={
                      formik.values.ermDateFinish != null && formik.values.ermDateStart === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="ermDateStart"
                      name="ermDateStart"
                      selected={formik.values.ermDateStart}
                      onChange={(date: Date | null) => formik.setFieldValue('ermDateStart', date)}
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="From"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                  <div
                    className={
                      formik.values.ermDateStart != null && formik.values.ermDateFinish === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="ermDateFinish"
                      name="ermDateFinish"
                      selected={formik.values.ermDateFinish}
                      onChange={(date: Date | null) => formik.setFieldValue('ermDateFinish', date)}
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="To"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showFields.qrrDate && (
              <div className="col-md-3">
                <label className="col-form-label">{t('qrrDate')}</label>
                <div className="input-group input-group-date-picker-wrapper">
                  <div
                    className={
                      formik.values.qrrDateFinish != null && formik.values.qrrDateStart === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="qrrDateStart"
                      name="qrrDateStart"
                      selected={formik.values.qrrDateStart}
                      onChange={(date: Date | null) => formik.setFieldValue('qrrDateStart', date)}
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="From"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                  <div
                    className={
                      formik.values.qrrDateStart != null && formik.values.qrrDateFinish === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="qrrDateFinish"
                      name="qrrDateFinish"
                      selected={formik.values.qrrDateFinish}
                      onChange={(date: Date | null) => formik.setFieldValue('qrrDateFinish', date)}
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="To"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showFields.approvalDate && (
              <div className="col-md-3">
                <label className="col-form-label">{t('approvalDate')}</label>
                <div className="input-group input-group-date-picker-wrapper">
                  <div
                    className={
                      formik.values.approvalDateFinish != null &&
                      formik.values.approvalDateStart === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="approvalDateStart"
                      name="approvalDateStart"
                      selected={formik.values.approvalDateStart}
                      onChange={(date: Date | null) =>
                        formik.setFieldValue('approvalDateStart', date)
                      }
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="From"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                  <div
                    className={
                      formik.values.approvalDateStart != null &&
                      formik.values.approvalDateFinish === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="approvalDateFinish"
                      name="approvalDateFinish"
                      selected={formik.values.approvalDateFinish}
                      onChange={(date: Date | null) =>
                        formik.setFieldValue('approvalDateFinish', date)
                      }
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="To"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showFields.closingDate && (
              <div className="col-md-3">
                <label className="col-form-label">{t('closingDate')}</label>
                <div className="input-group input-group-date-picker-wrapper">
                  <div
                    className={
                      formik.values.closingDateFinish != null &&
                      formik.values.closingDateStart === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="closingDateStart"
                      name="closingDateStart"
                      selected={formik.values.closingDateStart}
                      onChange={(date: Date | null) =>
                        formik.setFieldValue('closingDateStart', date)
                      }
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="From"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                  <div
                    className={
                      formik.values.closingDateStart != null &&
                      formik.values.closingDateFinish === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="closingDateFinish"
                      name="closingDateFinish"
                      selected={formik.values.closingDateFinish}
                      onChange={(date: Date | null) =>
                        formik.setFieldValue('closingDateFinish', date)
                      }
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="To"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-row">
            {showFields.approvalDate && (
              <div className="col-md-3">
                <label className="col-form-label">{t('queryDate')}</label>
                <div className="input-group input-group-date-picker-wrapper">
                  <div
                    className={
                      formik.values.queryDate != null && formik.values.queryDate === null
                        ? FORM_DATE_PICKER_REQUIRED
                        : FORM_DATE_PICKER
                    }
                  >
                    <DatePicker
                      id="queryDate"
                      name="queryDate"
                      selected={formik.values.queryDate}
                      onChange={(date: Date | null) => formik.setFieldValue('queryDate', date)}
                      dateFormat="dd/MM/yyy"
                      className="form-control-date-picker"
                      placeholderText="From"
                    />
                    <div className="form__form-group-icon">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="modal__footer modal__foote--custom-filter">
        <hr />
        <Spinner loading={formik.isSubmitting} />
        <Button
          key="btnCancel"
          className="btn-tertiary"
          onClick={onCancel}
          text={t('cancel')}
          type="button"
          disabled={formik.isSubmitting}
        />
        <Button
          key="btnApplyAndSave"
          className="btn-secondary"
          text={t('applyAndSaveText')}
          type="button"
          onClick={applyAndSaveFilterHandler}
          disabled={formik.isSubmitting}
        />
        <Button
          key="btnApply"
          className="btn-primary"
          text={t('applyText')}
          type="button"
          onClick={applyFilterHandler}
          disabled={formik.isSubmitting}
        />
      </div>
    </form>
  );
};

export default CustomFiltersForm;
